<template>
  <div class="msec">
    <div class="msec__head">
      <div class="msec__title">
        <h4 class="mb-0">Карта объектов</h4>
        <span class="text-muted small ml-2">{{ countLabel }}</span>
      </div>
      <div class="msec__chips" role="group" aria-label="Фильтр по состоянию">
        <button
          v-for="s in states"
          :key="s.key"
          type="button"
          class="msec__chip"
          :class="{ 'msec__chip--on': filters[s.key] }"
          :aria-pressed="String(!!filters[s.key])"
          @click="toggleFilter(s.key)"
        >
          <span class="msec__chip-dot" :style="{ background: s.color }"></span>{{ s.label }}
        </button>
      </div>
      <button type="button" class="btn btn-sm btn-outline-secondary msec__panel-toggle" @click="togglePanel">
        {{ panelOpen ? 'Скрыть панель' : 'Показать панель' }}
      </button>
    </div>

    <div v-if="failedResources.length" class="alert alert-danger py-2 px-3 mb-2 d-flex align-items-center" role="alert">
      <span>Не удалось загрузить: {{ failedResources.map((f) => f.label).join(', ') }}.</span>
      <button type="button" class="btn btn-sm btn-light ml-auto msec__retry" @click="retryFailed">Повторить</button>
    </div>

    <div v-if="!booted" class="msec__loading">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Загрузка…</span></div>
    </div>

    <div v-else class="msec__grid" :class="{ 'msec__grid--full': !panelOpen }">
      <div class="msec__map">
        <ObjectsMap
          ref="map"
          :objects="mapObjects"
          :points="mapPoints"
          :pick-mode="pickMode"
          @select-object="selectObject"
          @select-point="selectPoint"
          @pick="onPick"
          @pick-cancel="cancelPick"
        >
          <ObjectCard
            v-if="selectedLocation"
            class="msec__card"
            :location="selectedLocation"
            :active-task="cardTask"
            :extra-active-count="extraActiveCount"
            :tasks-unavailable="tasksUnavailable"
            :tasks-state="tasksState"
            :busy="busy"
            @close="clearSelection"
            @create-task="openCreateTask"
            @edit-task="openEditTask"
            @cancel-task="cancelTask"
            @show-active-tasks="showActiveTasks"
          />
          <PointCard
            v-if="selectedPoint"
            class="msec__card"
            :point="selectedPoint"
            :group="groupsById[selectedPoint.group_id] || null"
            :active-task="cardTask"
            :extra-active-count="extraActiveCount"
            :tasks-unavailable="tasksUnavailable"
            :tasks-state="tasksState"
            :busy="busy"
            @close="clearSelection"
            @edit="openEditPoint"
            @delete="deletePoint"
            @create-task="openCreateTask"
            @edit-task="openEditTask"
            @cancel-task="cancelTask"
            @show-active-tasks="showActiveTasks"
          />
        </ObjectsMap>
      </div>
      <aside v-if="panelOpen" class="msec__panel">
        <ActiveTasksPanel
          :tasks="activeTaskList"
          :focus-targets="focusTargets"
          :status="activeTasks.status"
          :open="activeTasksOpen"
          @toggle="activeTasksOpen = !activeTasksOpen"
          @focus="focusTask"
          @edit="openEditTask"
          @cancel="cancelTask"
          @retry="reloadTasksAndLocations"
        />
        <CustomPointsPanel
          :groups="groups.data"
          :points="customPoints.data"
          :groups-status="groups.status"
          :points-status="customPoints.status"
          :hidden-group-ids="hiddenGroupIds"
          :layer-on="layerOn"
          :open="pointsOpen"
          @toggle="pointsOpen = !pointsOpen"
          @add-point="openCreatePoint"
          @edit-point="openEditPoint"
          @add-group="openCreateGroup"
          @edit-group="openEditGroup"
          @delete-group="deleteGroup"
          @toggle-group="toggleGroup"
          @toggle-layer="toggleLayer"
          @focus-point="focusPoint"
          @delete-point="deletePoint"
          @retry="reloadGroups"
        />
      </aside>
    </div>

    <TaskFormModal
      v-if="taskModal"
      :key="taskModal.mode + '-' + (taskModal.draftId || (taskModal.task && taskModal.task.id))"
      :mode="taskModal.mode"
      :location="taskModal.location"
      :task="taskModal.task"
      :draft-id="taskModal.draftId"
      :engineers="engineers.data"
      :work-types="workTypes.data"
      @saved="onTaskSaved"
      @closed="onTaskClosed"
    />

    <GroupFormModal
      v-if="groupModal"
      :key="'group-' + (groupModal.group ? groupModal.group.id : 'new')"
      :group="groupModal.group"
      @saved="onGroupSaved"
      @closed="groupModal = null"
    />

    <PointFormModal
      v-if="pointModal"
      :key="'point-' + (pointModal.point ? pointModal.point.id : 'new') + '-' + (pointModal.form ? 'restored' : 'fresh')"
      :point="pointModal.point"
      :groups="groups.data"
      :initial-form="pointModal.form"
      @saved="onPointSaved"
      @closed="onPointClosed"
      @pick-on-map="startPick"
    />
  </div>
</template>

<script>
import ObjectsMap from '@/components/management/dispatcher/map/ObjectsMap.vue';
import ObjectCard from '@/components/management/dispatcher/map/ObjectCard.vue';
import TaskFormModal from '@/components/management/dispatcher/map/TaskFormModal.vue';
import ActiveTasksPanel from '@/components/management/dispatcher/map/ActiveTasksPanel.vue';
import CustomPointsPanel from '@/components/management/dispatcher/map/CustomPointsPanel.vue';
import GroupFormModal from '@/components/management/dispatcher/map/GroupFormModal.vue';
import PointFormModal from '@/components/management/dispatcher/map/PointFormModal.vue';
import PointCard from '@/components/management/dispatcher/map/PointCard.vue';
import * as api from '@/components/management/dispatcher/api';
import { createResource, loadResource, isReady } from '@/components/management/dispatcher/resource';
import {
  OBJECT_STATES, objectFlags, resolveObjectState, stateMeta, hasValidCoords, toLatLng, groupColor,
} from '@/components/management/dispatcher/objectState';
import { notifySuccess, notifyError } from '@/components/management/dispatcher/notify';
import { ensureReferences } from '@/components/management/dispatcher/references';
import {
  isActiveTask, cancelTaskText, CANCEL_TASK_OK, DRAFT_CLOSE_TEXT, DRAFT_CLOSE_OK,
} from '@/components/management/dispatcher/taskPayload';

// A map drag ends with a DOM click on the same element; past this distance it is not a click on the card's outside.
const CLICK_SLOP_PX = 6;

const RESOURCE_LABELS = {
  locations: 'объекты',
  activeTasks: 'задачи',
  customPoints: 'дополнительные точки',
  groups: 'группы',
};

export default {
  name: 'MapSection',
  components: {
    ObjectsMap,
    ObjectCard,
    TaskFormModal,
    ActiveTasksPanel,
    CustomPointsPanel,
    GroupFormModal,
    PointFormModal,
    PointCard,
  },
  props: {
    layoutVersion: { type: Number, default: 0 },
  },
  data() {
    const filters = {};
    OBJECT_STATES.forEach((s) => { filters[s.key] = true; });
    return {
      states: OBJECT_STATES,
      locations: createResource(),
      customPoints: createResource(),
      groups: createResource(),
      activeTasks: createResource(),
      engineers: createResource(),
      workTypes: createResource(),
      booted: false,
      filters,
      selectedObjectId: null,
      selectedPointId: null,
      panelOpen: true,
      hiddenGroupIds: [],
      layerOn: true,
      pickMode: false,
      taskModal: null,       // { mode, location, task, draftId }
      groupModal: null,      // { group }
      pointModal: null,      // { point, form }
      pickDraft: null,       // { point, form } while the map is in pick mode
      pointsOpen: false,     // right panel points card state
      busy: false,           // section-level lock for task mutations
      activeTasksOpen: true, // right panel card state (used by Task 14)
    };
  },
  computed: {
    locationsById() {
      const out = {};
      this.locations.data.forEach((l) => { out[l.id] = l; });
      return out;
    },
    groupsById() {
      const out = {};
      this.groups.data.forEach((g) => { out[g.id] = g; });
      return out;
    },
    activeTasksByLocationId() {
      const out = {};
      this.activeTaskList.forEach((t) => {
        (out[t.geo_location_id] = out[t.geo_location_id] || []).push(t);
      });
      return out;
    },
    activeTaskList() {
      return this.activeTasks.data.filter(isActiveTask);
    },
    focusTargets() {
      const out = {};
      this.activeTaskList.forEach((t) => { out[t.id] = this.focusTargetFor(t); });
      return out;
    },
    tasksUnavailable() {
      return !isReady(this.locations) || !isReady(this.activeTasks);
    },
    /** What the card says while the actions are unavailable: a reload is not a failed load (spec §12). */
    tasksState() {
      if (this.locations.status === 'error' || this.activeTasks.status === 'error') return 'error';
      return this.tasksUnavailable ? 'loading' : 'ready';
    },
    objectsWithCoords() {
      return this.locations.data.filter(hasValidCoords);
    },
    noCoordsCount() {
      return this.locations.data.length - this.objectsWithCoords.length;
    },
    /** A chip shows every object carrying that flag, so an object with several flags stays visible while any of them is on. */
    visibleLocations() {
      return this.objectsWithCoords.filter((l) => objectFlags(l).some((k) => this.filters[k]));
    },
    countLabel() {
      const base = `${this.visibleLocations.length} из ${this.objectsWithCoords.length} объектов`;
      return this.noCoordsCount > 0 ? `${base} · без координат: ${this.noCoordsCount}` : base;
    },
    mapObjects() {
      return this.visibleLocations.map((l) => {
        const meta = stateMeta(l);
        return {
          id: l.id,
          latLng: toLatLng(l),
          name: l.name || '—',
          stateLabel: meta.label,
          color: meta.color,
          hasTask: this.hasActiveTask(l),
          selected: l.id === this.selectedObjectId,
        };
      });
    },
    visiblePoints() {
      if (!this.layerOn) return [];
      return this.customPoints.data.filter((p) => hasValidCoords(p) && !this.hiddenGroupIds.includes(p.group_id));
    },
    mapPoints() {
      return this.visiblePoints.map((p) => ({
        id: p.id,
        latLng: toLatLng(p),
        name: p.name || '—',
        color: groupColor(this.groupsById[p.group_id]),
        hasTask: (this.activeTasksByLocationId[p.id] || []).length > 0,
        selected: p.id === this.selectedPointId,
      }));
    },
    selectedLocation() {
      return this.selectedObjectId === null ? null : (this.locationsById[this.selectedObjectId] || null);
    },
    selectedPoint() {
      return this.selectedPointId === null ? null : (this.customPoints.data.find((p) => p.id === this.selectedPointId) || null);
    },
    /** Whatever the open card shows: an object or a custom point (both are geo-locations to a task). */
    selectedEntity() {
      return this.selectedLocation || this.selectedPoint;
    },
    cardTask() {
      const loc = this.selectedEntity;
      if (!loc) return null;
      const list = this.activeTasksByLocationId[loc.id] || [];
      const lastId = loc.last_assigned_task ? loc.last_assigned_task.id : null;
      return list.find((t) => t.id === lastId) || list[0] || null;
    },
    extraActiveCount() {
      const loc = this.selectedEntity;
      if (!loc) return 0;
      return Math.max(0, (this.activeTasksByLocationId[loc.id] || []).length - 1);
    },
    failedResources() {
      return ['locations', 'activeTasks', 'customPoints', 'groups']
        .filter((key) => this[key].status === 'error')
        .map((key) => ({ key, label: RESOURCE_LABELS[key] }));
    },
  },
  watch: {
    layoutVersion() {
      this.invalidateMap();
    },
  },
  created() {
    this.inFlight = {}; // non-reactive: de-duplicates concurrent reloads
    this.pressAt = null; // non-reactive: where the last mousedown landed (drag guard)
    this.loadInitial();
  },
  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('mousedown', this.onDocumentMousedown, true);
    document.addEventListener('click', this.onDocumentClick, true);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('mousedown', this.onDocumentMousedown, true);
    document.removeEventListener('click', this.onDocumentClick, true);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasOpenDraft()) return next();
    next(false);
    const draftId = this.taskModal.draftId;
    this.confirm(DRAFT_CLOSE_TEXT, DRAFT_CLOSE_OK).then((ok) => {
      if (!ok) return null;
      return api.rejectTaskDraft(draftId).then(() => {
        this.taskModal = null;
        this.$router.push(to.fullPath);
      }, (e) => notifyError(this, e));
    }).catch(() => {}); // $router.push rejects on an aborted/duplicated navigation
    return undefined;
  },
  methods: {
    /* ---------- loading ---------- */
    async loadInitial() {
      await Promise.all([
        loadResource(this.locations, api.getEnrichedLocations),
        loadResource(this.customPoints, api.getCustomPoints),
        loadResource(this.groups, api.getGroups),
        loadResource(this.activeTasks, api.getActiveTasks),
      ]);
      this.booted = true;
      this.focusFromQuery();
    },
    dedupe(key, run) {
      if (this.inFlight[key]) return this.inFlight[key];
      const promise = run().finally(() => { this.inFlight[key] = null; });
      this.inFlight[key] = promise;
      return promise;
    },
    reloadTasksAndLocations() {
      return this.dedupe('tasksLocations', () => Promise.all([
        loadResource(this.locations, api.getEnrichedLocations),
        loadResource(this.activeTasks, api.getActiveTasks),
      ]).then(() => this.pruneSelection()));
    },
    reloadPoints() {
      return this.dedupe('points', () => loadResource(this.customPoints, api.getCustomPoints).then(() => this.pruneSelection()));
    },
    reloadGroups() {
      return this.dedupe('groups', () => loadResource(this.groups, api.getGroups));
    },
    retryFailed() {
      const jobs = [];
      if (this.locations.status === 'error' || this.activeTasks.status === 'error') jobs.push(this.reloadTasksAndLocations());
      if (this.customPoints.status === 'error') jobs.push(this.reloadPoints());
      if (this.groups.status === 'error') jobs.push(this.reloadGroups());
      return Promise.all(jobs);
    },
    pruneSelection() {
      if (this.selectedObjectId !== null && !this.locationsById[this.selectedObjectId]) this.selectedObjectId = null;
      if (this.selectedPointId !== null && !this.customPoints.data.some((p) => p.id === this.selectedPointId)) this.selectedPointId = null;
    },
    hasActiveTask(location) {
      return !!location.last_assigned_task || (this.activeTasksByLocationId[location.id] || []).length > 0;
    },
    /* ---------- ui state ---------- */
    toggleFilter(key) {
      this.$set(this.filters, key, !this.filters[key]);
    },
    selectObject(id) {
      this.selectedObjectId = id;
      this.selectedPointId = null;
    },
    selectPoint(id) {
      this.selectedPointId = id;
      this.selectedObjectId = null;
    },
    clearSelection() {
      this.selectedObjectId = null;
      this.selectedPointId = null;
    },
    /* ---------- click outside the card closes it (spec §9) ---------- */
    onDocumentMousedown(e) {
      this.pressAt = { x: e.clientX, y: e.clientY };
    },
    // Capture phase, so it runs before a marker's own click handler: clicking another marker
    // clears the old selection first and the new one still wins. Clicks inside the card, inside
    // any modal (forms, confirm boxes, toasts) and while picking coordinates are left alone.
    onDocumentClick(e) {
      if (this.selectedObjectId === null && this.selectedPointId === null) return;
      if (this.pickMode) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.msec__card, .modal, .modal-backdrop, .b-toaster')) return;
      const p = this.pressAt;
      if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > CLICK_SLOP_PX) return; // a drag, not a click
      this.clearSelection();
    },
    togglePanel() {
      this.panelOpen = !this.panelOpen;
      this.invalidateMap();
    },
    invalidateMap() {
      this.$nextTick(() => {
        const map = this.$refs.map;
        if (map && typeof map.invalidateSize === 'function') map.invalidateSize();
      });
    },
    /* ---------- «На карте» (spec §8) ---------- */
    focusTargetFor(task) {
      const loc = this.locationsById[task.geo_location_id];
      if (loc && hasValidCoords(loc)) return { ok: true, kind: 'object', id: loc.id };
      const point = this.customPoints.data.find((p) => p.id === task.geo_location_id);
      if (point && hasValidCoords(point)) return { ok: true, kind: 'point', id: point.id };
      return { ok: false, kind: null, id: null };
    },
    focusTask(task) {
      const target = this.focusTargetFor(task);
      if (!target.ok) return;
      if (target.kind === 'object') this.revealObject(this.locationsById[target.id]);
      else this.revealPoint(this.customPoints.data.find((p) => p.id === target.id));
    },
    /** `?focus=<geo_location_id>` from the tracker's «На карте» (KOP-38 spec §7): reveal once on boot, then drop the query. */
    focusFromQuery() {
      const query = (this.$route && this.$route.query) || {};
      if (!Object.prototype.hasOwnProperty.call(query, 'focus')) return;
      const id = /^\d+$/.test(String(query.focus)) ? Number(query.focus) : null;
      const location = id === null ? null : this.locationsById[id];
      const point = id === null ? null : this.customPoints.data.find((p) => p.id === id);
      if (location && hasValidCoords(location)) this.revealObject(location);
      else if (point && hasValidCoords(point)) this.revealPoint(point);
      else notifyError(this, 'Объект не найден на карте');
      const { focus, ...rest } = query;
      this.$router.replace({ query: rest }).catch(() => {});
    },
    revealObject(location) {
      this.$set(this.filters, resolveObjectState(location), true);
      this.selectObject(location.id);
      this.$nextTick(() => {
        const map = this.$refs.map;
        if (map && typeof map.panTo === 'function') map.panTo(toLatLng(location), 15);
      });
    },
    /* ---------- tasks ---------- */
    confirm(text, okTitle) {
      return this.$bvModal.msgBoxConfirm(text, { okTitle, cancelTitle: 'Отмена', centered: true }).then((v) => v === true);
    },
    ensureReferences() {
      return ensureReferences(this);
    },
    async openCreateTask(location) {
      if (this.tasksUnavailable || this.taskModal || this.busy || !location) return;
      this.busy = true;
      try {
        if (!(await this.ensureReferences())) return;
        const draft = await api.createTaskDraft();
        if (!draft || !draft.id) throw new Error('Сервер не вернул id черновика');
        this.taskModal = {
          mode: 'create', location, task: null, draftId: Number(draft.id),
        };
      } catch (e) {
        notifyError(this, e, 'Не удалось создать черновик задачи');
      } finally {
        this.busy = false;
      }
    },
    async openEditTask(task) {
      if (this.tasksUnavailable || this.taskModal || this.busy || !task) return;
      const id = task.geo_location_id;
      const location = this.locationsById[id] || this.customPoints.data.find((p) => p.id === id) || task.geo_location || null;
      if (!location) {
        notifyError(this, 'Объект задачи не найден');
        return;
      }
      this.busy = true;
      try {
        if (await this.ensureReferences()) {
          this.taskModal = {
            mode: 'edit', location, task, draftId: null,
          };
        }
      } finally {
        this.busy = false;
      }
    },
    async cancelTask(task) {
      if (this.tasksUnavailable || this.busy || !task) return;
      this.busy = true; // held across the dialog so a second click is ignored
      try {
        if (!(await this.confirm(cancelTaskText(task.id), CANCEL_TASK_OK))) return;
        await api.blockTask(task.id);
        notifySuccess(this, `Задача T‑${task.id} отменена`);
        await this.reloadTasksAndLocations();
      } catch (e) {
        notifyError(this, e);
      } finally {
        this.busy = false;
      }
    },
    onTaskSaved() {
      this.taskModal = null;
      return this.reloadTasksAndLocations();
    },
    onTaskClosed() {
      this.taskModal = null;
    },
    showActiveTasks() {
      this.panelOpen = true;
      this.activeTasksOpen = true;
      this.invalidateMap();
    },
    hasOpenDraft() {
      return !!(this.taskModal && this.taskModal.mode === 'create');
    },
    onBeforeUnload(e) {
      if (!this.hasOpenDraft()) return undefined;
      e.preventDefault();
      e.returnValue = DRAFT_CLOSE_TEXT;
      return DRAFT_CLOSE_TEXT;
    },
    /* ---------- groups ---------- */
    openCreateGroup() {
      if (!isReady(this.groups) || this.busy) return;
      this.groupModal = { group: null };
    },
    openEditGroup(group) {
      if (!isReady(this.groups) || this.busy) return;
      this.groupModal = { group };
    },
    async deleteGroup(group) {
      if (!isReady(this.groups) || this.busy || !group) return;
      this.busy = true; // held across the dialog so a second click is ignored
      try {
        if (!(await this.confirm(`Удалить группу «${group.title || ''}»?`, 'Удалить'))) return;
        await api.deleteGroup(group.id);
        notifySuccess(this, 'Группа удалена');
        this.hiddenGroupIds = this.hiddenGroupIds.filter((id) => id !== group.id);
        await this.reloadGroups();
      } catch (e) {
        notifyError(this, e);
      } finally {
        this.busy = false;
      }
    },
    onGroupSaved() {
      this.groupModal = null;
      return this.reloadGroups();
    },
    toggleGroup(id) {
      this.hiddenGroupIds = this.hiddenGroupIds.includes(id)
        ? this.hiddenGroupIds.filter((x) => x !== id)
        : [...this.hiddenGroupIds, id];
    },
    toggleLayer() {
      this.layerOn = !this.layerOn;
    },
    /* ---------- points: focus & delete ---------- */
    focusPoint(point) {
      if (!point || !hasValidCoords(point)) return;
      this.revealPoint(point);
    },
    revealPoint(point) {
      this.layerOn = true;
      this.hiddenGroupIds = this.hiddenGroupIds.filter((id) => id !== point.group_id);
      this.selectPoint(point.id);
      this.$nextTick(() => {
        const map = this.$refs.map;
        if (map && typeof map.panTo === 'function') map.panTo(toLatLng(point), 15);
      });
    },
    async deletePoint(point) {
      if (!isReady(this.customPoints) || this.busy || !point) return;
      this.busy = true; // held across the dialog so a second click is ignored
      try {
        if (!(await this.confirm(`Удалить точку «${point.name || ''}»?`, 'Удалить'))) return;
        await api.deletePoint(point.id);
        notifySuccess(this, 'Точка удалена');
        if (this.selectedPointId === point.id) this.selectedPointId = null;
        await this.reloadPoints();
      } catch (e) {
        notifyError(this, e);
      } finally {
        this.busy = false;
      }
    },
    /* ---------- points: form & pick mode (spec §8, §11.4) ---------- */
    canEditPoints() {
      return isReady(this.customPoints) && isReady(this.groups);
    },
    // An open form is a modal overlay, so it cannot be re-opened over itself; a pick in progress
    // hides the form but leaves the panel clickable, so that is the case worth guarding — and the
    // pick holds `busy` too, which is what keeps the rest of the panel out of the way.
    openCreatePoint() {
      if (!this.canEditPoints() || this.busy || this.pickMode) return;
      this.pointModal = { point: null, form: null };
    },
    openEditPoint(point) {
      if (!this.canEditPoints() || this.busy || this.pickMode || !point) return;
      this.pointModal = { point, form: null };
    },
    onPointSaved() {
      this.pointModal = null;
      return this.reloadPoints();
    },
    onPointClosed() {
      this.pointModal = null;
    },
    // A pick takes the section lock: the form is off screen but the panel is not, and any modal
    // opened over a pick would make Esc both close it and cancel the pick (re-mounting the form).
    startPick(form) {
      if (!this.pointModal) return;
      this.pickDraft = { point: this.pointModal.point, form: { ...form } };
      this.pointModal = null;
      this.pickMode = true;
      this.busy = true;
    },
    onPick(coords) {
      this.endPick();
      if (this.pickDraft) {
        const form = { ...this.pickDraft.form, latitude: coords.lat.toFixed(6), longitude: coords.lng.toFixed(6) };
        this.pointModal = { point: this.pickDraft.point, form };
      }
      this.pickDraft = null;
    },
    cancelPick() {
      this.endPick();
      if (this.pickDraft) this.pointModal = { point: this.pickDraft.point, form: this.pickDraft.form };
      this.pickDraft = null;
    },
    /** Releases the lock before the form is restored, so re-opening it is never blocked by it. */
    endPick() {
      this.pickMode = false;
      this.busy = false;
    },
  },
};
</script>

<style scoped lang="scss">
.msec { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.msec__head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding-bottom: 10px; }
.msec__title { display: flex; align-items: baseline; }
.msec__chips { display: flex; gap: 6px; flex-wrap: wrap; margin-left: auto; }
.msec__chip {
  appearance: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px; font-weight: 700; padding: 5px 10px; border-radius: 20px;
  border: 1px solid #dcded8; background: #fff; color: #8b918c;
  &--on { border-color: #17876f; background: #f2f9f6; color: #0f5a4a; }
  &:focus-visible { outline: 2px solid #17876f; outline-offset: 1px; }
}
.msec__chip-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
.msec__loading { flex: 1; display: flex; align-items: center; justify-content: center; }
.msec__grid { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr) 344px; gap: 12px; }
.msec__grid--full { grid-template-columns: minmax(0, 1fr); }
.msec__map { position: relative; min-height: 0; background: #e9ebe6; border-radius: 8px; }
.msec__card { position: absolute; top: 12px; right: 12px; z-index: 1001; max-height: calc(100% - 24px); overflow: auto; }
.msec__panel { min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; }
</style>
